import React, { useState, useEffect } from 'react';
import { ArrowLeft, Star, Truck, Check, ShoppingCart } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import BottomNav from '../components/common/BottomNav';
import NotificationBell from '../components/common/NotificationBell';
import CartIcon from '../components/cart/CartIcon';
import GroupAdminPanel from '../components/groups/GroupAdminPanel';
import { useNotification } from '../context/NotificationContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { generateDemoGroups, generateSimilarGroups } from '../utils/groupData';

const GroupDetailPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { addNotification } = useNotification();
  const { addToCart, isInCart } = useCart();
  
  // NEW: Detect vendor context
  const [vendorContext, setVendorContext] = useState(false);
  
  const [isJoined, setIsJoined] = useState(() => {
    // Check if already joined from localStorage
    const joinedGroups = localStorage.getItem('joinedGroups');
    return joinedGroups ? JSON.parse(joinedGroups).includes(id) : false;
  });
  const [joinLoading, setJoinLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userLocation, setUserLocation] = useState(null);
  const [quantity, setQuantity] = useState(1);

  // NEW: Detect vendor context on mount
  useEffect(() => {
    // Check if we came from vendor routes or if user is vendor
    const userType = localStorage.getItem('userType');
    const isVendor = userType === 'vendor';
    const fromVendor = location.state?.fromVendor || 
                     document.referrer.includes('/vendor/') ||
                     window.location.search.includes('vendor=true');
    
    setVendorContext(isVendor || fromVendor);
  }, [location]);

  // Get user location (same as HomePage)
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            city: "Sacramento",
            country: "USA",
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          });
        },
        (error) => {
          setUserLocation({
            city: "Sacramento",
            country: "USA", 
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          });
        }
      );
    } else {
      setUserLocation({
        city: "Sacramento",
        country: "USA",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
    }
  }, []);

  // Get the correct group data - check both main groups AND similar groups
  const allGroups = generateDemoGroups(userLocation);
  let groupData = allGroups.find(group => group.id === id);
  
  // If not found in main groups, check if it's a similar group
  if (!groupData && id.startsWith('similar_')) {
    const parts = id.split('_');
    if (parts.length >= 2) {
      const originalGroupId = parts[1];
      const originalGroup = allGroups.find(g => g.id === originalGroupId);
      
      if (originalGroup) {
        const similarGroups = generateSimilarGroups(originalGroup, userLocation);
        groupData = similarGroups.find(g => g.id === id);
      }
    }
  }

  // Real-time countdown updates
  useEffect(() => {
    if (!groupData) return;

    const updateFrequency = () => {
      const now = new Date();
      const difference = groupData.endDate - now;
      const hoursLeft = difference / (1000 * 60 * 60);
      
      return hoursLeft <= 1 && hoursLeft > 0 ? 1000 : 60000;
    };
    
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, updateFrequency());

    return () => clearInterval(timer);
  }, [groupData]);

  // If group not found, show error or redirect
  if (!groupData) {
    return (
      <div className="container mx-auto px-4 pb-20 bg-gray-50 min-h-screen">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-600 mb-2">Group Not Found</h2>
            <p className="text-gray-500 mb-4">The group you're looking for doesn't exist.</p>
            <button 
              onClick={() => navigate(vendorContext ? '/vendor/dashboard' : '/')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg"
            >
              {vendorContext ? 'Back to Vendor Dashboard' : 'Back to Home'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if group is completed
  const isGroupCompleted = () => {
    const now = new Date();
    return groupData.isCompleted || groupData.endDate <= now || groupData.progress >= 100;
  };

  // Real-time countdown calculation
  const calculateTimeLeft = (endDate) => {
    const now = new Date();
    const difference = endDate - now;
    
    if (difference > 0) {
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      
      const totalHours = difference / (1000 * 60 * 60);
      
      if (totalHours <= 1) {
        return `${minutes} min ${seconds} sec`;
      } else if (totalHours <= 6) {
        return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} min`;
      } else if (days > 0) {
        return `${days} day${days !== 1 ? 's' : ''}`;
      } else {
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
      }
    }
    return 'COMPLETED';
  };

  // Get urgency color for styling
  const getUrgencyColor = (endDate) => {
    const now = new Date();
    const difference = endDate - now;
    const hoursLeft = difference / (1000 * 60 * 60);
    
    if (difference <= 0) return 'green';
    if (hoursLeft <= 6) return 'red';
    if (hoursLeft <= 48) return 'yellow';
    return 'blue';
  };

  // Calculate token rewards with bulk bonuses
  const calculateTokenReward = (baseTokens, quantity) => {
    let bonus = 1;
    if (quantity >= 5) bonus = 1.5;
    else if (quantity >= 3) bonus = 1.25;
    
    return Math.floor(baseTokens * quantity * bonus);
  };

  // Calculate bulk discount
  const getBulkDiscount = (basePrice, quantity) => {
    let discount = 0;
    if (quantity >= 5) discount = 0.05;
    else if (quantity >= 3) discount = 0.03;
    
    return basePrice * (1 - discount);
  };

  // FIXED: Smart back navigation based on context
  const handleGoBack = () => {
    if (vendorContext) {
      // Return to vendor dashboard or discovery page
      const fromDiscovery = location.state?.fromVendorDiscovery;
      if (fromDiscovery) {
        navigate('/vendor/groups');
      } else {
        navigate('/vendor/dashboard');
      }
    } else {
      // Regular member navigation
      navigate('/groups');
    }
  };

  const handleJoin = async () => {
    // Only allow joining if not in vendor context (vendors are viewing, not joining)
    if (vendorContext) {
      addNotification({
        type: 'info',
        title: 'Vendor Mode',
        message: 'You are viewing this group as a vendor. Switch to member mode to join groups.',
        showToast: true
      });
      return;
    }

    setJoinLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsJoined(true);
      
      const joinedGroups = localStorage.getItem('joinedGroups');
      const groups = joinedGroups ? JSON.parse(joinedGroups) : [];
      if (!groups.includes(id)) {
        groups.push(id);
        localStorage.setItem('joinedGroups', JSON.stringify(groups));
      }
      
      addNotification({
        type: 'tokens_earned',
        title: 'Group Joined!',
        message: `Successfully joined ${groupData.title}! You'll earn ${calculateTokenReward(groupData.tokenReward, quantity)} tokens when you purchase.`,
        showToast: true
      });
    } catch (error) {
      console.error('Failed to join group');
    } finally {
      setJoinLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (vendorContext) {
      addNotification({
        type: 'info',
        title: 'Vendor Mode',
        message: 'You are viewing this group as a vendor. Switch to member mode to purchase.',
        showToast: true
      });
      return;
    }

    const cartItem = {
      id: groupData.id,
      groupId: groupData.id,
      title: groupData.title,
      description: groupData.description,
      currentPrice: groupData.currentPrice,
      image: groupData.image,
      vendor: groupData.vendor,
      tokenReward: groupData.tokenReward,
      category: groupData.category,
      quantity: quantity
    };
    
    addToCart(cartItem);
  };

  const handleLeaveGroup = async () => {
    if (vendorContext) return; // Vendors can't leave groups

    if (!window.confirm(`Are you sure you want to leave "${groupData.title}"? You'll lose your spot and any potential savings.`)) {
      return;
    }
    
    const joinedGroups = localStorage.getItem('joinedGroups');
    if (joinedGroups) {
      const groups = JSON.parse(joinedGroups);
      const updatedGroups = groups.filter(groupId => groupId !== id);
      localStorage.setItem('joinedGroups', JSON.stringify(updatedGroups));
      
      setIsJoined(false);
      
      if (isInCart(groupData.id, groupData.id)) {
        const cartItems = JSON.parse(localStorage.getItem(`cart_${localStorage.getItem('userId') || 'default'}`) || '[]');
        const updatedCart = cartItems.filter(item => item.groupId !== id);
        localStorage.setItem(`cart_${localStorage.getItem('userId') || 'default'}`, JSON.stringify(updatedCart));
      }
      
      localStorage.removeItem(`cart_${id}_data`);
      window.dispatchEvent(new Event('groupLeft'));
      
      addNotification({
        type: 'info',
        title: 'Left Group',
        message: `You've left "${groupData.title}". You can rejoin anytime before it closes.`,
        showToast: true
      });
    }
  };

  const handleViewLogistics = () => {
    if (vendorContext) {
      navigate('/vendor/dashboard', { state: { activeTab: 'jobs' } });
    } else {
      navigate('/logistics');
    }
  };

  // NEW: Handle vendor bulk order creation
  const handleCreateBulkOrder = () => {
    if (vendorContext) {
      navigate(`/vendor/bulk-order/${groupData.id}`);
    }
  };

  const urgencyColor = getUrgencyColor(groupData.endDate);
  const groupCompleted = isGroupCompleted();

  // Determine if user should see admin panel (only for non-vendor context)
  const isGroupAdmin = !vendorContext && user && groupData && (
    (groupData.creatorId && groupData.creatorId === user.id) ||
    (groupData.creatorName && groupData.creatorName === user.name) ||
    (groupData.isUserCreated === true)
  );

  return (
    <div className="container mx-auto px-4 pb-20 bg-gray-50 min-h-screen">
      {/* Header with context-aware navigation */}
      <div className="flex justify-between items-center mt-6 mb-4">
        <div className="flex items-center">
          <button 
            className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow mr-3"
            onClick={handleGoBack}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold">Group Details</h1>
            {vendorContext && (
              <p className="text-sm text-blue-600">Vendor View • Business Intelligence</p>
            )}
          </div>
        </div>
        <div className="flex items-center">
          {!vendorContext && <CartIcon />}
          <NotificationBell />
          <div className="flex items-center bg-yellow-100 rounded-full px-3 py-1 ml-3">
            <span className="font-bold mr-1">124</span>
            <span className="text-yellow-600">⭐</span>
          </div>
        </div>
      </div>

      {/* Vendor Context Banner */}
      {vendorContext && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900">Vendor Intelligence View</h3>
              <p className="text-sm text-blue-700">
                Analyzing group potential • {groupData.currentCount} members • {groupData.progress}% complete
              </p>
            </div>
            <button
              onClick={handleCreateBulkOrder}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Create Bulk Offer
            </button>
          </div>
        </div>
      )}
      
      {/* Main Group Card */}
      <div className="bg-white rounded-lg shadow overflow-hidden mb-4">
        <div className="h-40 bg-gray-200 flex items-center justify-center relative overflow-hidden">
          <img 
            src={groupData.image} 
            alt={groupData.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <span className="text-gray-500 absolute inset-0 flex items-center justify-center" style={{display: 'none'}}>
            Product Image
          </span>
          
          {groupData.isSimilarGroup && (
            <div className="absolute top-2 right-2">
              <div className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                SIMILAR GROUP
              </div>
            </div>
          )}
        </div>
        
        <div className="p-4">
          <h3 className="font-bold text-lg mb-2">{groupData.title}</h3>
          
          {/* Price Section */}
          <div className="flex justify-between mb-2">
            <div>
              Current Price: <strong>${groupData.currentPrice.toFixed(2)}</strong>
              {!groupCompleted && !vendorContext && quantity >= 3 && (
                <span className="ml-2 text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                  BULK HERO {quantity >= 5 ? '5%' : '3%'} EXTRA OFF!
                </span>
              )}
            </div>
            <div className="text-green-600">Save {groupData.savings}%</div>
          </div>
          
          {/* Token Reward - only show for members */}
          {!vendorContext && (
            <div className="flex items-center mb-3">
              <div className="inline-flex items-center bg-yellow-100 px-2 py-1 rounded-full">
                <span className="mr-1 font-semibold text-sm">Earn {calculateTokenReward(groupData.tokenReward, quantity)}</span>
                <Star size={14} className="text-yellow-600" />
              </div>
              <span className="text-xs text-gray-600 ml-2">
                tokens for {quantity} item{quantity !== 1 ? 's' : ''}
                {quantity >= 3 && (
                  <span className="text-orange-600 font-semibold"> (+{quantity >= 5 ? '50' : '25'}% bonus!)</span>
                )}
              </span>
            </div>
          )}
          
          {/* Progress Section */}
          <div className="text-sm text-blue-600 mb-2">
            Next break: ${groupData.nextBreakPrice} at {groupData.nextBreakCount} units
          </div>
          
          <div className="bg-gray-200 h-2 rounded-full mb-1 relative">
            <div 
              className={`h-2 rounded-full ${groupCompleted ? 'bg-green-500' : 'bg-blue-600'}`}
              style={{ width: `${groupData.progress}%` }}
            ></div>
            {isJoined && quantity > 1 && !groupCompleted && !vendorContext && (
              <div 
                className="h-2 bg-yellow-400 absolute top-0 rounded-full"
                style={{ 
                  left: `${groupData.progress}%`,
                  width: `${Math.min(100 - groupData.progress, (quantity / groupData.nextBreakCount * 100))}%`,
                  opacity: 0.6
                }}
              ></div>
            )}
          </div>
          
          <div className="flex justify-between text-sm text-gray-500 mb-4">
            <div>
              {groupData.currentUnits || groupData.currentCount}/{groupData.nextBreakCount} units 
              <span className="text-xs">({groupData.currentCount} people)</span>
            </div>
            <div>
              {groupCompleted ? 'Group Succeeded!' : (
                <span 
                  className={`${
                    urgencyColor === 'red' 
                      ? 'text-red-600 animate-pulse'
                      : urgencyColor === 'yellow' 
                      ? 'text-yellow-600' 
                      : urgencyColor === 'green'
                      ? 'text-green-600'
                      : 'text-gray-500'
                  }`}
                >
                  Ends in {calculateTimeLeft(groupData.endDate)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Product Info Card */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <h3 className="font-semibold mb-2">Product Details</h3>
        <p className="text-sm text-gray-700 mb-2">{groupData.description}</p>
        <div className="text-sm text-gray-600">
          <div><strong>Vendor:</strong> {groupData.vendor}</div>
          <div><strong>Location:</strong> Sacramento, CA</div>
          <div><strong>Category:</strong> {groupData.category}</div>
        </div>
      </div>
      
      {/* Admin Panel - only show for group creators in member mode */}
      {isGroupAdmin && (
        <GroupAdminPanel 
          group={groupData} 
          onUpdate={(updates) => {
            console.log('Group updates:', updates);
          }}
        />
      )}

      {/* Pickup Info Card */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <h3 className="font-semibold mb-2">Pickup Information</h3>
        <div className="text-sm text-gray-600 space-y-1">
          <div><strong>Location:</strong> {groupData.pickupLocation}</div>
          <div><strong>Address:</strong> {groupData.pickupAddress}</div>
          <div><strong>Time:</strong> {groupData.pickupTime}</div>
        </div>
        <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-blue-800">
          📍 Located in Sacramento, CA - Local community pickup point
        </div>
      </div>

      {/* Quantity Selector - only for joined members */}
      {isJoined && !groupCompleted && !vendorContext && (
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <h3 className="font-semibold mb-3">Select Quantity</h3>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button 
                className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-300"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
              >
                −
              </button>
              <span className="mx-4 font-semibold text-xl">{quantity}</span>
              <button 
                className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-300"
                onClick={() => setQuantity(Math.min(10, quantity + 1))}
              >
                +
              </button>
            </div>
            <div className="text-right">
              <div className="font-semibold text-lg">
                ${(getBulkDiscount(groupData.currentPrice, quantity) * quantity).toFixed(2)}
              </div>
              {quantity >= 3 && (
                <div className="text-xs text-green-600">
                  Save ${(groupData.currentPrice * quantity * (quantity >= 5 ? 0.05 : 0.03)).toFixed(2)}
                </div>
              )}
            </div>
          </div>
          {quantity > 1 && (
            <div className="text-xs text-blue-600 mt-3 p-2 bg-blue-50 rounded">
              🚀 Your {quantity} units help move the group closer to the next price break!
              {quantity >= 3 && (
                <div className="text-orange-600 font-semibold mt-1">
                  ⭐ BULK HERO STATUS: You're helping the entire group succeed!
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Action Buttons - Context Aware */}
      <div className="space-y-3">
        {vendorContext ? (
          // Vendor Actions
          <div className="space-y-2">
            <button
              onClick={handleCreateBulkOrder}
              className="w-full bg-blue-600 text-white rounded-lg py-3 font-semibold flex items-center justify-center"
            >
              Create Bulk Offer for This Group
            </button>
            <button
              onClick={() => navigate('/vendor/groups')}
              className="w-full border border-blue-600 text-blue-600 rounded-lg py-3 font-semibold"
            >
              Discover More Groups
            </button>
          </div>
        ) : (
          // Member Actions
          <>
            {!groupCompleted && !isJoined ? (
              <button
                onClick={handleJoin}
                disabled={joinLoading}
                className="w-full bg-blue-600 text-white rounded-lg py-3 font-semibold flex items-center justify-center disabled:opacity-50"
              >
                {joinLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Joining...
                  </div>
                ) : (
                  'Join Group'
                )}
              </button>
            ) : !groupCompleted && isJoined ? (
              <div className="space-y-2">
                <div className="w-full bg-green-50 border border-green-200 text-green-600 rounded-lg py-3 font-semibold flex items-center justify-center">
                  <Check size={18} className="mr-2" />
                  Successfully Joined!
                </div>
                
                <button
                  onClick={handleAddToCart}
                  className={`w-full py-3 rounded-lg font-semibold flex items-center justify-center ${
                    isInCart(groupData.id, groupData.id)
                      ? 'bg-gray-200 text-gray-600'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  <ShoppingCart size={18} className="mr-2" />
                  {isInCart(groupData.id, groupData.id) ? 'Update Cart' : 'Add to Cart'}
                </button>
                
                <button 
                  className="w-full border border-blue-600 text-blue-600 rounded-lg py-3 font-semibold flex items-center justify-center"
                  onClick={handleViewLogistics}
                >
                  <Truck size={18} className="mr-2" />
                  View Pickup Details
                </button>
                
                <button
                  onClick={handleLeaveGroup}
                  className="w-full border border-red-300 text-red-600 rounded-lg py-3 font-semibold hover:bg-red-50 transition-colors"
                >
                  Leave Group
                </button>
              </div>
            ) : (
              <div className="w-full bg-green-50 border border-green-200 text-green-600 rounded-lg py-3 font-semibold flex items-center justify-center">
                <Check size={18} className="mr-2" />
                Group Completed Successfully!
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Only show BottomNav for members */}
      {!vendorContext && <BottomNav />}
    </div>
  );
};

export default GroupDetailPage;